import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IPagedCollection } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportCategory } from '../../models/Support/ISupportCategory';
import { emptyOnForbidden, supportQueryRetry } from '../../utils/Support/supportHelpers';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS } from './supportQueryHelpers';

interface UseCategoryMutationOptions {
	onSuccess?: () => void;
	onError?: (error: unknown) => void;
}

export function useSupportCategoriesQuery(mailboxId: string) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.mailboxes.categories(mailboxId),
		staleTime: 30_000,
		retry: supportQueryRetry,
		queryFn: () =>
			emptyOnForbidden<IPagedCollection<ISupportCategory>>(
				() =>
					userSession.webServiceHelper.callAsync<IPagedCollection<ISupportCategory>>(
						SUPPORT_PATHS.mailboxes.categories(mailboxId),
						'GET'
					),
				{ values: [] }
			),
		enabled: !!mailboxId,
		meta: { toastErrorMessage: 'Failed to load categories' },
	});
}

export function useCreateCategoryMutation(mailboxId: string, { onSuccess, onError }: UseCategoryMutationOptions = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (category: Omit<ISupportCategory, 'id' | 'mailboxId'>) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.categories(mailboxId), 'POST', category),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.categories(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}

export function useUpdateCategoryMutation(mailboxId: string, { onSuccess, onError }: UseCategoryMutationOptions = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, ...data }: Omit<ISupportCategory, 'mailboxId'>) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.category(mailboxId, id), 'PUT', data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.categories(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}

export function useDeleteCategoryMutation(mailboxId: string, { onSuccess, onError }: UseCategoryMutationOptions = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.category(mailboxId, id), 'DELETE'),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.categories(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}
